import React, { useState } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { X } from 'lucide-react';
import { sendGoogleAdsConversion } from '../utils/googleAds';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/myknggva';

export default function ContactFormModal({ open, onClose, defaultSubject = '' }) {
  const { theme } = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState(''); // 'sending' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMessage('');
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: name || undefined,
          email: email || undefined,
          _subject: subject || 'Contact / Support',
          message: message || '(No message)',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && !data.error) {
        if (/enterprise/i.test(subject)) sendGoogleAdsConversion('enterpriseLead', { dedupeKey: `enterprise:${email.trim().toLowerCase()}`, email });
        setStatus('success');
        setName('');
        setEmail('');
        setSubject('');
        setMessage('');
      } else {
        setStatus('error');
        setErrorMessage(data.error || data.errors?.map((e) => e.message).join(' ') || 'Something went wrong.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message || 'Failed to send. Please try again.');
    }
  };

  const handleClose = () => {
    setStatus('');
    setErrorMessage('');
    onClose();
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-form-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        backgroundColor: 'rgba(0,0,0,0.5)',
      }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 440,
          backgroundColor: theme.card,
          borderRadius: 16,
          border: `1px solid ${theme.border}`,
          boxShadow: theme.shadow,
          padding: 24,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 id="contact-form-title" style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.text }}>
            Contact support
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            style={{
              padding: 6,
              border: 'none',
              background: 'transparent',
              color: theme.textMuted,
              cursor: 'pointer',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={22} />
          </button>
        </div>

        {status === 'success' ? (
          <div style={{ padding: '24px 0', textAlign: 'center' }}>
            <p style={{ margin: 0, color: theme.text, fontSize: 15 }}>
              Thanks! We’ll get back to you soon.
            </p>
            <button
              type="button"
              onClick={handleClose}
              style={{
                marginTop: 16,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                color: theme.primaryContrast,
                backgroundColor: theme.primary,
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="contact-name" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 4 }}>
                Name (optional)
              </label>
              <input
                id="contact-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: 14,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 8,
                  backgroundColor: theme.inputBg || theme.card,
                  color: theme.text,
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="contact-email" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 4 }}>
                Email
              </label>
              <input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: 14,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 8,
                  backgroundColor: theme.inputBg || theme.card,
                  color: theme.text,
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="contact-subject" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 4 }}>
                Subject
              </label>
              <input
                id="contact-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="What is this about?"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: 14,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 8,
                  backgroundColor: theme.inputBg || theme.card,
                  color: theme.text,
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="contact-message" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: theme.text, marginBottom: 4 }}>
                Message
              </label>
              <textarea
                id="contact-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="How can we help?"
                required
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: 14,
                  border: `1px solid ${theme.inputBorder}`,
                  borderRadius: 8,
                  backgroundColor: theme.inputBg || theme.card,
                  color: theme.text,
                  resize: 'vertical',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                }}
              />
            </div>
            {status === 'error' && errorMessage && (
              <p style={{ margin: '0 0 12px', fontSize: 13, color: theme.errorText || theme.usageError }}>
                {errorMessage}
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleClose}
                style={{
                  padding: '10px 18px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.textMuted,
                  backgroundColor: 'transparent',
                  border: `1px solid ${theme.border}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={status === 'sending'}
                style={{
                  padding: '10px 18px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: theme.primaryContrast,
                  backgroundColor: theme.primary,
                  border: 'none',
                  borderRadius: 8,
                  cursor: status === 'sending' ? 'wait' : 'pointer',
                  opacity: status === 'sending' ? 0.8 : 1,
                }}
              >
                {status === 'sending' ? 'Sending…' : 'Send'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
